import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { FilterBar, FilterPills, FilterSelect } from '@/components/feature/FilterBar';
import AiGeneratorModal from '@/pages/gcse/mock-exams/components/AiGeneratorModal';
import MockPaperCard from '@/pages/gcse/mock-exams/components/MockPaperCard';

export type Board = 'AQA' | 'OCR' | 'Edexcel' | 'WJEC';

export interface MockPaper {
  id: string;
  board: Board;
  paper: string;
  title: string;
  set: string;
  marks: number;
  duration: string;
  topics: string[];
  difficulty: 'Foundation' | 'Higher' | 'Mixed';
  downloads: number;
  rating: number;
  hasMarkScheme: boolean;
}

export const mockPapers: MockPaper[] = [
  {
    id: 'mp1', board: 'AQA', paper: 'Paper 1', title: 'Mock Exam — Set A',
    set: 'Set A', marks: 80, duration: '1h 30m',
    topics: ['Algorithms', 'Programming', 'Data Structures'],
    difficulty: 'Mixed', downloads: 5821, rating: 4.8, hasMarkScheme: true,
  },
  {
    id: 'mp2', board: 'AQA', paper: 'Paper 2', title: 'Mock Exam — Set A',
    set: 'Set A', marks: 80, duration: '1h 30m',
    topics: ['Computer Systems', 'Networks', 'Cyber Security'],
    difficulty: 'Mixed', downloads: 5203, rating: 4.7, hasMarkScheme: true,
  },
  {
    id: 'mp3', board: 'AQA', paper: 'Paper 1', title: 'Mock Exam — Set B',
    set: 'Set B', marks: 80, duration: '1h 30m',
    topics: ['Searching & Sorting', 'Python Programming', 'Recursion'],
    difficulty: 'Higher', downloads: 4156, rating: 4.6, hasMarkScheme: true,
  },
  {
    id: 'mp4', board: 'AQA', paper: 'Paper 2', title: 'Mock Exam — Set B',
    set: 'Set B', marks: 80, duration: '1h 30m',
    topics: ['CPU Architecture', 'Data Representation', 'Software Development'],
    difficulty: 'Higher', downloads: 3987, rating: 4.5, hasMarkScheme: true,
  },
  {
    id: 'mp5', board: 'AQA', paper: 'Paper 1', title: 'Mock Exam — Set C',
    set: 'Set C', marks: 80, duration: '1h 30m',
    topics: ['Boolean Logic', 'Assembly Language', 'Computational Thinking'],
    difficulty: 'Foundation', downloads: 3412, rating: 4.4, hasMarkScheme: false,
  },
  {
    id: 'mp6', board: 'OCR', paper: 'Paper 1', title: 'Mock Exam — Set A',
    set: 'Set A', marks: 80, duration: '1h 30m',
    topics: ['Systems Architecture', 'Memory & Storage', 'Network Security'],
    difficulty: 'Mixed', downloads: 2841, rating: 4.7, hasMarkScheme: true,
  },
  {
    id: 'mp7', board: 'OCR', paper: 'Paper 2', title: 'Mock Exam — Set A',
    set: 'Set A', marks: 80, duration: '1h 30m',
    topics: ['Algorithms', 'Programming Fundamentals', 'Computational Thinking'],
    difficulty: 'Mixed', downloads: 2508, rating: 4.6, hasMarkScheme: true,
  },
  {
    id: 'mp8', board: 'OCR', paper: 'Paper 1', title: 'Mock Exam — Set B',
    set: 'Set B', marks: 80, duration: '1h 30m',
    topics: ['Data Representation', 'Computer Systems', 'Ethical Issues'],
    difficulty: 'Foundation', downloads: 1976, rating: 4.5, hasMarkScheme: true,
  },
  {
    id: 'mp9', board: 'Edexcel', paper: 'Paper 1', title: 'Mock Exam — Set A',
    set: 'Set A', marks: 80, duration: '1h 30m',
    topics: ['Computational Thinking', 'Data Representation', 'Computer Systems'],
    difficulty: 'Mixed', downloads: 1876, rating: 4.5, hasMarkScheme: true,
  },
  {
    id: 'mp10', board: 'Edexcel', paper: 'Paper 2', title: 'Mock Exam — Set A',
    set: 'Set A', marks: 80, duration: '1h 30m',
    topics: ['Programming', 'Algorithms', 'Networks'],
    difficulty: 'Higher', downloads: 1543, rating: 4.4, hasMarkScheme: false,
  },
  {
    id: 'mp11', board: 'WJEC', paper: 'Paper 1', title: 'Mock Exam — Set A',
    set: 'Set A', marks: 80, duration: '1h 30m',
    topics: ['Programming', 'Algorithms', 'Data Representation'],
    difficulty: 'Mixed', downloads: 1234, rating: 4.4, hasMarkScheme: true,
  },
  {
    id: 'mp12', board: 'WJEC', paper: 'Paper 2', title: 'Mock Exam — Set A',
    set: 'Set A', marks: 80, duration: '1h 30m',
    topics: ['Computer Systems', 'Networks', 'Cyber Security'],
    difficulty: 'Foundation', downloads: 987, rating: 4.3, hasMarkScheme: true,
  },
];

const boards: Board[] = ['AQA', 'OCR', 'Edexcel', 'WJEC'];

export default function GcseMockExamsPage() {
  const [selectedBoard, setSelectedBoard] = useState<Board | 'All'>('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('All');
  const [showAiModal, setShowAiModal] = useState(false);

  const filtered = mockPapers.filter((p) => {
    if (selectedBoard !== 'All' && p.board !== selectedBoard) return false;
    if (selectedDifficulty !== 'All' && p.difficulty !== selectedDifficulty) return false;
    return true;
  });

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-slate-900 pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20academic%20background%20with%20glowing%20exam%20paper%20and%20pencil%20silhouettes%2C%20deep%20navy%20gradient%2C%20geometric%20grid%20patterns%2C%20clean%20minimal%20design%20with%20emerald%20and%20white%20light%20accents%20on%20dark%20background%2C%20sophisticated%20study%20atmosphere&width=1440&height=300&seq=gcse-mock1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">GCSE Resources</span>
            <i className="ri-arrow-right-s-line text-slate-500 text-xs"></i>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Mock Exams &amp; Revision</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-2">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
                GCSE Mock Exams &amp; Revision
              </h1>
              <p className="text-slate-400 text-base max-w-2xl mt-2">
                Practice with full mock exam papers or generate a personalised paper with AI — tailored to your exam board, topics, and difficulty level.
              </p>
            </div>
            <button
              onClick={() => setShowAiModal(true)}
              className="flex items-center gap-2 px-5 py-3 bg-emerald-500 hover:bg-emerald-400 text-white rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              <i className="ri-sparkling-line text-base"></i>
              Generate with AI
            </button>
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <div className="bg-slate-800/70 rounded-lg px-4 py-2.5 flex items-center gap-2">
              <i className="ri-file-paper-2-line text-emerald-400 text-sm"></i>
              <span className="text-white text-sm font-medium">{mockPapers.length} mock papers</span>
            </div>
            <div className="bg-slate-800/70 rounded-lg px-4 py-2.5 flex items-center gap-2">
              <i className="ri-check-double-line text-emerald-400 text-sm"></i>
              <span className="text-white text-sm font-medium">Mark schemes included</span>
            </div>
            <div className="bg-slate-800/70 rounded-lg px-4 py-2.5 flex items-center gap-2">
              <i className="ri-sparkling-line text-emerald-400 text-sm"></i>
              <span className="text-white text-sm font-medium">AI paper generator</span>
            </div>
          </div>
        </div>
      </section>

      {/* AI Banner */}
      <div className="bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                <i className="ri-sparkling-2-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white font-bold text-sm">AI Paper Generator</p>
                <p className="text-emerald-100 text-xs">Create a custom mock exam in seconds — choose your board, topics, difficulty and number of questions.</p>
              </div>
            </div>
            <button
              onClick={() => setShowAiModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 rounded-lg font-semibold text-sm hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              <i className="ri-magic-line text-sm"></i>
              Try AI Generator
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <FilterBar count={filtered.length} countLabel="papers">
        <FilterPills
          label="Exam Board"
          value={selectedBoard}
          onChange={(v) => setSelectedBoard(v as Board | 'All')}
          options={[
            { value: 'All', label: 'All Boards' },
            ...boards.map((b) => ({ value: b, label: b })),
          ]}
        />
        <div className="sm:ml-auto">
          <FilterSelect
            label="Difficulty"
            value={selectedDifficulty}
            onChange={(v) => setSelectedDifficulty(v)}
            options={[
              { value: 'All', label: 'All Levels' },
              { value: 'Foundation', label: 'Foundation' },
              { value: 'Higher', label: 'Higher' },
              { value: 'Mixed', label: 'Mixed' },
            ]}
          />
        </div>
      </FilterBar>

      {/* Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 md:px-6 py-10 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((paper) => (
            <MockPaperCard key={paper.id} paper={paper} />
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-2xl mb-4">
              <i className="ri-file-search-line text-slate-400 text-2xl"></i>
            </div>
            <p className="text-slate-600 font-medium mb-1">No papers found</p>
            <p className="text-slate-400 text-sm">Try adjusting your filters</p>
          </div>
        )}
      </main>

      <Footer />

      {showAiModal && <AiGeneratorModal onClose={() => setShowAiModal(false)} level="GCSE" />}
    </div>
  );
}
